import { FederalAgeCategory, FederalClassification } from '@shared/enums/domain.enum';
import { Users } from 'lucide-react';

interface FfpjpFiltersSectionProps {
  allowedAgeCategories: FederalAgeCategory[];
  allowedClassifications: FederalClassification[];
  onAgeCategoriesChange: (cats: FederalAgeCategory[]) => void;
  onClassificationsChange: (cls: FederalClassification[]) => void;
}

const DISABLED_AGE_CATEGORIES = new Set([
  FederalAgeCategory.BENJAMIN,
  FederalAgeCategory.MINIME,
  FederalAgeCategory.CADET,
]);

export function FfpjpFiltersSection({
  allowedAgeCategories,
  allowedClassifications,
  onAgeCategoriesChange,
  onClassificationsChange,
}: FfpjpFiltersSectionProps) {
  return (
    <div className='rounded-2xl border border-slate-200 bg-white p-8'>
      <h2 className='mb-1 flex items-center gap-2 text-lg font-bold text-slate-900'>
        <Users className='h-5 w-5 text-emerald-600' />
        Filtres f&eacute;d&eacute;raux (FFPJP)
      </h2>
      <p className='mb-6 text-sm text-slate-500'>
        Laissez vide pour accepter toutes les cat&eacute;gories et classifications. Si des filtres sont d&eacute;finis,
        les joueurs devront avoir leur date de naissance renseign&eacute;e.
      </p>
      <div className='grid gap-8 md:grid-cols-2'>
        {/* Catégories d'âge */}
        <div>
          <p className='mb-3 text-sm font-semibold text-slate-700'>
            Cat&eacute;gories d&apos;&acirc;ge autoris&eacute;es
          </p>
          <div className='space-y-2'>
            {/* Feature 1 (2026-08) — « Open » : aucune restriction de catégorie
                d'âge (= toutes acceptées, liste vide). Pré-coché par défaut.
                Cocher une catégorie précise le décoche automatiquement
                (allowedAgeCategories.length devient > 0). */}
            <label className='flex cursor-pointer items-center gap-3 rounded-lg bg-emerald-50 px-2 py-1.5'>
              <input
                type='checkbox'
                checked={allowedAgeCategories.length === 0}
                onChange={() => onAgeCategoriesChange([])}
                className='h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500'
              />
              <span className='text-sm font-semibold text-emerald-700'>
                Open - Toutes cat&eacute;gories accept&eacute;es
              </span>
            </label>
            {Object.values(FederalAgeCategory).map(cat => {
              const isDisabled = DISABLED_AGE_CATEGORIES.has(cat);
              return (
                <label
                  key={cat}
                  className={`group relative flex items-center gap-3 ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                >
                  <input
                    type='checkbox'
                    disabled={isDisabled}
                    checked={allowedAgeCategories.includes(cat)}
                    onChange={e => {
                      const next = e.target.checked
                        ? [...allowedAgeCategories, cat]
                        : allowedAgeCategories.filter(c => c !== cat);
                      onAgeCategoriesChange(next);
                    }}
                    className='h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 disabled:cursor-not-allowed'
                  />
                  <span className='text-sm text-slate-700'>{cat}</span>
                  {isDisabled && (
                    <span className='pointer-events-none absolute -top-8 left-1/2 z-10 hidden -translate-x-1/2 items-center whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white shadow-lg group-hover:flex'>
                      Bientôt disponible
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        </div>

        {/* Classifications */}
        <div>
          <p className='mb-3 text-sm font-semibold text-slate-700'>Classifications autoris&eacute;es</p>
          <div className='space-y-2'>
            {Object.values(FederalClassification).map(cls => (
              <label key={cls} className='flex cursor-pointer items-center gap-3'>
                <input
                  type='checkbox'
                  checked={allowedClassifications.includes(cls)}
                  onChange={e => {
                    const next = e.target.checked
                      ? [...allowedClassifications, cls]
                      : allowedClassifications.filter(c => c !== cls);
                    onClassificationsChange(next);
                  }}
                  className='h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500'
                />
                <span className='text-sm text-slate-700'>{cls}</span>
              </label>
            ))}
          </div>
          {allowedClassifications.length === 0 && (
            <p className='mt-2 text-xs text-slate-400'>Toutes classifications accept&eacute;es</p>
          )}
        </div>
      </div>
    </div>
  );
}
